// Contact name projection — the ONE place the surname / full-name split is
// expressed (SEP-174 / SEP-175 / SEP-180).
//
// Three surfaces need the same two strings about the same person:
//   - the Contacts directory (surname column, then the full Name column);
//   - the Suitability Records directory (the SUR's primary contact, as Last
//     Name then Full Name);
//   - the relationship picker's SUR option label (`SUR-1005 · <surname> · …`).
//
// It lives in `packages/contract` because the SHAPE is wire truth — all three
// row DTOs project the identical pair, so they compose this schema instead of
// each spelling `lastName` / `fullName` again — and because the derivation is
// a pure presentation projection with no ontology or DB dependency, exactly
// like the other compute modules in this package
// (`aml-assessment-compute.ts`, `suitability-assessment-compute.ts`). The
// server calls it at the mapping seam; the web layer can call it for any
// label it composes locally, without a second, drifting implementation.

import { z } from 'zod';

/**
 * A contact's name, split for display: the surname alone (sortable A-Z, the
 * first column of the Contacts and SUR directories) and the full display name.
 * Either may be null — an organisation contact has no surname, and a contact
 * outside the caller's read scope has neither (the id is kept, the name is
 * withheld; see the involved-parties projection).
 */
export const contactNameProjectionSchema = z.object({
    lastName: z.string().nullable(),
    fullName: z.string().nullable(),
});
export type ContactNameProjection = z.infer<typeof contactNameProjectionSchema>;

/** The `Contact` fields the projection reads. All optional — an ingest row may
 *  carry only the recomputed display `name`. */
export interface ContactNameParts {
    /** `Contact.name` — the display full name, recomputed from the parts on write. */
    readonly name?: string | null;
    readonly firstName?: string | null;
    readonly lastName?: string | null;
}

function clean(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * Best-effort surname from a display name, used ONLY when the contact carries
 * no structured `lastName` (a hand-created row, or a pre-backfill ingest row
 * that only ever had a display name). Two forms are recognised:
 *
 *   - `"Ashworth, Mr Julian"` — CRM inverted form: everything before the first
 *     comma is the surname;
 *   - `"Mr Julian Ashworth"` — the trailing whitespace-separated token.
 *
 * Deliberately simple and deliberately a FALLBACK: it gets `van der Meer`
 * wrong, which is why the structured `lastName` always wins when present. A
 * single-token name yields itself.
 */
export function surnameFromDisplayName(displayName: string | null | undefined): string | null {
    const name = clean(displayName);
    if (!name) return null;
    const comma = name.indexOf(',');
    if (comma >= 0) return clean(name.slice(0, comma));
    const tokens = name.split(/\s+/);
    return clean(tokens[tokens.length - 1]);
}

/**
 * Project a PERSON contact's name. `fullName` prefers the stored display name
 * and falls back to the structured parts; `lastName` prefers the structured
 * surname and falls back to `surnameFromDisplayName`.
 */
export function projectContactName(parts: ContactNameParts): ContactNameProjection {
    const stored = clean(parts.name);
    const first = clean(parts.firstName);
    const last = clean(parts.lastName);
    const fullName = stored ?? clean([first, last].filter((part) => part !== null).join(' '));
    return { lastName: last ?? surnameFromDisplayName(fullName), fullName };
}

/**
 * Project an ORGANISATION contact's name. An organisation has no surname —
 * deriving one from the legal name would put `Ltd` in the surname column — so
 * `lastName` is null by construction and the whole legal name is the label.
 */
export function projectOrganisationName(
    organisationName: string | null | undefined,
): ContactNameProjection {
    return { lastName: null, fullName: clean(organisationName) };
}

/** The name of a party the caller may not read: the row stays, the name is
 *  withheld (the involved-parties rule — see `clients.ts` `involvedClients`). */
export const WITHHELD_CONTACT_NAME: ContactNameProjection = { lastName: null, fullName: null };
